import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shopService } from './index';
import { toast } from 'react-toastify';

// Get all shops
export const useShops = () => {
  return useQuery({
    queryKey: ['shops'],
    queryFn: shopService.getAllShops,
    select: (data) => data.data,
  });
};

// Get shops by owner
export const useShopsByOwner = (ownerId) => {
  return useQuery({
    queryKey: ['shops', 'owner', ownerId],
    queryFn: () => shopService.getShopsByOwner(ownerId),
    select: (data) => data.data,
    enabled: !!ownerId,
  });
};

// Get shop by ID
export const useShop = (id) => {
  return useQuery({
    queryKey: ['shops', id],
    queryFn: () => shopService.getShopById(id),
    select: (data) => data.data,
    enabled: !!id,
  });
};

// Get verified shops
export const useVerifiedShops = () => {
  return useQuery({
    queryKey: ['shops', 'verified'],
    queryFn: shopService.getVerifiedShops,
    select: (data) => data.data,
  });
};

// Create shop mutation
export const useCreateShop = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: shopService.createShop,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['shops'] });
      toast.success('Shop created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create shop');
    },
  });
};

// Update shop mutation
export const useUpdateShop = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => shopService.updateShop(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shops'] });
      queryClient.invalidateQueries({ queryKey: ['shops', variables.id] });
      const body = response?.data;
      if (body?.verificationReset) {
        toast.warning(
          body.message ||
            'Location updated — an admin must verify your shop again before it appears publicly.'
        );
      } else {
        toast.success(body?.message || 'Shop updated successfully');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update shop');
    },
  });
};

// Verify shop mutation
export const useVerifyShop = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: shopService.verifyShop,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops'] });
      toast.success('Shop verified successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to verify shop');
    },
  });
};
